/**
 * API layer types.
 *
 * Design:
 *  - ApiError is a real class so `instanceof` checks work.
 *  - Subclasses pinpoint the failure category (auth, validation, network…).
 *  - isApiError() is the preferred type-guard for catch blocks.
 */

// ── Error classes ─────────────────────────────────────────────────────────────

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details ?? null;
  }
}

/** 400 — request body / query param validation failed */
export class ValidationError extends ApiError {
  readonly fields: Record<string, string[]>;

  constructor(
    message: string,
    fields: Record<string, string[]> = {},
    details?: unknown,
  ) {
    super(400, 'VALIDATION_ERROR', message, details);
    this.name = 'ValidationError';
    this.fields = fields;
  }
}

/** 401 — missing or expired token */
export class AuthError extends ApiError {
  constructor(message = 'Unauthorized', details?: unknown) {
    super(401, 'UNAUTHORIZED', message, details);
    this.name = 'AuthError';
  }
}

/** 403 — authenticated but not allowed */
export class ForbiddenError extends ApiError {
  constructor(message = 'Forbidden', details?: unknown) {
    super(403, 'FORBIDDEN', message, details);
    this.name = 'ForbiddenError';
  }
}

/** 404 — resource not found */
export class NotFoundError extends ApiError {
  constructor(message = 'Not found', details?: unknown) {
    super(404, 'NOT_FOUND', message, details);
    this.name = 'NotFoundError';
  }
}

/** 409 — conflict (e.g. duplicate slug/domain) */
export class ConflictError extends ApiError {
  constructor(message = 'Conflict', details?: unknown) {
    super(409, 'CONFLICT', message, details);
    this.name = 'ConflictError';
  }
}

/** 5xx or no response — server / network failure */
export class NetworkError extends ApiError {
  constructor(message = 'Network error', details?: unknown) {
    super(0, 'NETWORK_ERROR', message, details);
    this.name = 'NetworkError';
  }
}

// ── Type guards ───────────────────────────────────────────────────────────────

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

export function isAuthError(error: unknown): error is AuthError {
  return error instanceof AuthError;
}

export function isNotFoundError(error: unknown): error is NotFoundError {
  return error instanceof NotFoundError;
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

// ── Response shapes ───────────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface SortParams {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/** Used internally to return upload progress alongside the typed result */
export interface UploadResult<T> {
  data: T;
  uploadedBytes: number;
}
